import { useState } from 'react'

/**
 * Collapsible panel wrapper. Remembers open/closed state per `storageKey`
 * in localStorage so users don't lose their preferred view on refresh.
 *
 * Visual: white card with subtle shadow + small uppercase coloured
 * eyebrow title with a tinted underline (the design language from the
 * Genealogic style refresh). Pass a `color` to tint the eyebrow + line:
 *   green | gold | blue | amber | purple | red | grey (default green)
 */
const COLOR_MAP = {
  green:  { fg: '#3b6b5a', bg: 'rgba(59,107,90,0.18)'  },
  gold:   { fg: '#a8843e', bg: 'rgba(200,165,110,0.28)'},
  blue:   { fg: '#3a6e9a', bg: 'rgba(58,110,154,0.18)' },
  amber:  { fg: '#a06a18', bg: 'rgba(200,150,40,0.22)' },
  purple: { fg: '#6e3b9a', bg: 'rgba(110,59,154,0.18)' },
  red:    { fg: '#a04848', bg: 'rgba(160,72,72,0.18)'  },
  grey:   { fg: '#6b6b6b', bg: 'rgba(110,110,110,0.18)'},
}

export default function Collapsible({
  title, children, storageKey,
  defaultOpen = true, adornment = null, icon = null,
  color = 'green',
}) {
  const palette = COLOR_MAP[color] || COLOR_MAP.green
  const [open, setOpen] = useState(() => {
    if (!storageKey) return defaultOpen
    const saved = localStorage.getItem(`gl_coll_${storageKey}`)
    if (saved === 'open')   return true
    if (saved === 'closed') return false
    return defaultOpen
  })

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (storageKey) localStorage.setItem(`gl_coll_${storageKey}`, next ? 'open' : 'closed')
  }

  return (
    <div style={wrapStyle}>
      <button type="button" onClick={toggle} style={headerStyle(palette)} aria-expanded={open}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '.55rem' }}>
          {icon && <span aria-hidden style={iconBadgeStyle(palette)}>{icon}</span>}
          <span style={eyebrowStyle(palette)}>{title}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          {adornment}
          <span aria-hidden style={{
            fontSize: '.8rem',
            color: palette.fg,
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 180ms',
            display: 'inline-block',
            opacity: 0.65,
          }}>▸</span>
        </span>
      </button>
      <div style={dividerStyle(palette)} />
      {open && <div style={bodyStyle}>{children}</div>}
    </div>
  )
}

const wrapStyle = {
  background: '#fff',
  border: '1px solid var(--border, #e5e0d5)',
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(60, 50, 30, 0.04), 0 4px 14px rgba(60, 50, 30, 0.04)',
  overflow: 'hidden',
}

const headerStyle = (palette) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'transparent',
  border: 0,
  padding: '.95rem 1.25rem .55rem',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: palette.fg,
})

const eyebrowStyle = (palette) => ({
  fontSize: '.74rem',
  fontWeight: 700,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  color: palette.fg,
})

const iconBadgeStyle = (palette) => ({
  width: 22,
  height: 22,
  borderRadius: 6,
  background: palette.bg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '.85rem',
  flexShrink: 0,
})

const dividerStyle = (palette) => ({
  height: 1,
  margin: '0 1.25rem',
  background: `linear-gradient(90deg, ${palette.bg} 0%, transparent 100%)`,
})

const bodyStyle = {
  padding: '1rem 1.25rem 1.1rem',
}
